import {AsyncDataLoading} from '@frontend/components/AsyncDataLoading'
import {trans} from '@frontend/helpers'
import {mdiChevronLeft} from '@mdi/js'
import {
    useEffect,
    useMemo,
    useState,
} from 'react'
import {FadeSwitch} from 'swayok-react-mdb-ui-kit/components/Animation/FadeSwitch'
import {Card} from 'swayok-react-mdb-ui-kit/components/Card/Card'
import {CardBody} from 'swayok-react-mdb-ui-kit/components/Card/CardBody'
import {CardHeader} from 'swayok-react-mdb-ui-kit/components/Card/CardHeader'
import {Collapse} from 'swayok-react-mdb-ui-kit/components/Collapse/Collapse'
import {IconButton} from 'swayok-react-mdb-ui-kit/components/Icon/IconButton'
import {handleErrorResponse} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import {useUrlQueryParams} from 'swayok-react-mdb-ui-kit/helpers/useUrlQueryParams'
import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import {useAccountFlagsStore} from '../../../../stores'
import {
    ChatsApi,
    type ChatsMessagesListResponseData,
} from '../ChatsApi'
import type {
    ChatDetailsData,
    ChatMessageData,
    ChatsListUrlQueryParams,
} from '../ChatsModuleTypes'
import {ChatDetailsCardFooter} from './ChatDetailsCardFooter'
import {ChatDetailsCardHeaderInfo} from './ChatDetailsCardHeaderInfo'
import {ChatDetailsExecutorForAd} from './ChatDetailsExecutorForAd'
import {ChatDetailsWebsocketHandlers} from './ChatDetailsWebsocketHandlers'
import {ChatMessagesList} from './ChatMessagesList'

interface ChatDetailsCardProps {
    chat: ChatDetailsData | number
    // Отображение в мини-чате?
    miniChat?: boolean
    // Состояние выполнения действия с чатом.
    isProcessing?: boolean
    // Возврат к списку чатов.
    onBack: () => void
    // Удаление чата.
    onDelete: (chat: ChatDetailsData) => void
    // Прочитать все сообщения в чате.
    onReadAllMessages: (chat: ChatDetailsData) => void
    // Показать окно добавления отзыва о продавце.
    onAddReview: (chat: ChatDetailsData) => void
    // Загружены сообщения в чат.
    onLatestMessagesLoaded: (chat: ChatDetailsData) => void
}

const limitPerPage: number = 15

// Детали и список сообщений чата.
export function ChatDetailsCard(props: ChatDetailsCardProps) {

    const {
        chat: chatDataOrId,
        miniChat,
        isProcessing,
        onBack,
        onDelete,
        onReadAllMessages: propsOnReadAllMessages,
        onAddReview,
        onLatestMessagesLoaded,
    } = props

    const loadFlags = useAccountFlagsStore(
        state => state.loadFlags
    )

    // Параметры в URL Query.
    const {
        urlQueryParams,
        setUrlQueryParams,
    } = useUrlQueryParams<ChatsListUrlQueryParams>()
    // Данные чата.
    const [
        chat,
        setChat,
    ] = useState<ChatDetailsData | null>(
        typeof chatDataOrId === 'number' ? null : chatDataOrId
    )

    const [
        canSendImages,
        setCanSendImages,
    ] = useState<boolean>(false)

    // Сообщения чата
    const [
        messages,
        setMessages,
    ] = useState<ChatMessageData[]>([])
    // Состояния загрузки сообщений чата.
    const [
        isLoadingMessages,
        setIsLoadingMessages,
    ] = useState<boolean>(true)
    const [
        isMessagesLoadingError,
        setIsMessagesLoadingError,
    ] = useState<boolean>(false)
    const [
        isLoadingMoreMessages,
        setIsLoadingMoreMessages,
    ] = useState<boolean>(true)
    const [
        isNotFoundError,
        setIsNotFoundError,
    ] = useState<boolean>(false)
    const [
        isAllMessagesLoaded,
        setIsAllMessagesLoaded,
    ] = useState<boolean>(false)
    /*
     * Состояние возможности отправки запроса на прочтение новых сообщений в чате.
     * По умолчанию false т.к. функция должна отрабатывать, только если приходили
     * новые сообщения через websocket.
     */
    const [
        isReadingMessagesAllowed,
        setIsReadingMessagesAllowed,
    ] = useState<boolean>(false)

    // Найти ID чата.
    const chatId = useMemo((): number => {
        if (chat) {
            return chat.id
        }
        return typeof chatDataOrId === 'number' ? chatDataOrId : chatDataOrId.id
    }, [chat, chatDataOrId])

    // Загрузка или перезагрузка данных чата.
    const loadChatData = useEventCallback(() => {
        ChatsApi.getChatDetails(chatId)
            .then(responseData => {
                setChat(responseData.chat)
                setCanSendImages(responseData.canSendImages)
                if (responseData.chat.hasNotViewedMessages) {
                    // Возможно, нужно сбросить индикатор новых сообщений.
                    loadFlags(true).catch(() => {
                    })
                }
            })
            .catch((error: ApiError) => {
                handleErrorResponse(error)
                if (error.status === 404) {
                    setIsNotFoundError(false)
                }
            })
    })

    // Загрузка или перезагрузка сообщений чата.
    const loadChatMessages = useEventCallback((
        loadMore: boolean = false
    ): void => {
        if (loadMore && (isAllMessagesLoaded || isLoadingMoreMessages)) {
            return
        }
        setIsLoadingMessages(!loadMore)
        setIsLoadingMoreMessages(loadMore)
        setIsMessagesLoadingError(false)
        setIsNotFoundError(false)
        ChatsApi.getChatMessages(
            chatId,
            limitPerPage,
            loadMore && messages.length > 0 ? messages[0].id : null
        )
            .then((data: ChatsMessagesListResponseData) => {
                setMessages(
                    loadMore
                        ? ([] as ChatMessageData[]).concat(data.records, messages)
                        : data.records
                )
                setIsAllMessagesLoaded(data.records.length < limitPerPage)
                if (!loadMore && chat) {
                    onLatestMessagesLoaded(chat)
                }
            })
            .catch((error: ApiError) => {
                handleErrorResponse(error)
                if (!loadMore) {
                    setIsMessagesLoadingError(true)
                    if (error.status === 404) {
                        setIsNotFoundError(false)
                    }
                }
            })
            .finally(() => {
                setIsLoadingMessages(false)
                setIsLoadingMoreMessages(false)
            })
    })

    // В чат добавлено новое сообщение.
    const onNewMessageAdded = useEventCallback((
        message: ChatMessageData
    ): void => {
        setMessages((messages: ChatMessageData[]) => messages.concat([message]))
    })

    // Отметить все чужие сообщения в чате прочитанными.
    const onReadAllMessages = useEventCallback((
        chat: ChatDetailsData
    ): void => {
        propsOnReadAllMessages(chat)
        setMessages((messages: ChatMessageData[]) => {
            const updates: ChatMessageData[] = messages.slice()
            for (const item of updates) {
                if (!item.isAuthor) {
                    item.isViewed = true
                }
            }
            return updates
        })
    })

    // Отметить чужие сообщения, полученные через websocket от другого пользователя, прочитанными.
    const readNotViewedMessages = useEventCallback((): void => {
        if (!isReadingMessagesAllowed || isLoadingMessages || !chat) {
            // Предотвращаем спам и побочные эффекты.
            return
        }
        const ids: number[] = []
        for (const message of messages) {
            if (!message.isAuthor && !message.isViewed) {
                ids.push(message.id)
            }
        }
        setIsReadingMessagesAllowed(false)
        if (ids.length === 0) {
            return
        }
        ChatsApi.readSomeMessagesInChat(chat.id, ids)
            .then(() => {
                if (isLoadingMessages) {
                    return
                }
                // Отмечаем сообщения прочитанными.
                setMessages((messages: ChatMessageData[]) => {
                    const updates: ChatMessageData[] = messages.slice()
                    for (const item of updates) {
                        if (ids.includes(item.id)) {
                            item.isViewed = true
                        }
                    }
                    return updates
                })
            })
            .catch(() => {
                // Игнорируем ошибки.
            })
        // setIsReadingMessagesAllowed(true) не нужно вызывать в .finally()!
    })

    // Загрузка сообщений и данных чата при открытии.
    useEffect(() => {
        loadChatMessages()
        // Данные чата загружаются и тогда, когда чат передан объектом из списка:
        // только из них приходит разрешение отправлять фото.
        loadChatData()
        // Обновляем ID чата в URL, если отличается.
        if (
            !miniChat
            && urlQueryParams.get('chat') !== String(chatId)
        ) {
            urlQueryParams.set('chat', String(chatId))
            setUrlQueryParams(urlQueryParams)
        }
    }, [])

    return (
        <Card className="cabinet-chat-messages flex-container-column">
            <Collapse
                show={chat !== null}
                showImmediately
            >
                <CardHeader
                    className="py-3 px-0 mx-3 mx-sm-4 border-bottom flex-shrink-0 d-flex flex-row align-items-center gap-3 gap-sm-4"
                >
                    <IconButton
                        path={mdiChevronLeft}
                        onClick={onBack}
                        color="dark"
                        className="flex-shrink-0"
                    />
                    {chat && (
                        <ChatDetailsCardHeaderInfo
                            chat={chat}
                            miniChat={miniChat}
                            disabled={isProcessing}
                            onReadAllMessages={onReadAllMessages}
                            onDelete={onDelete}
                            onAddReview={onAddReview}
                        />
                    )}
                </CardHeader>
            </Collapse>
            <AsyncDataLoading
                className="flex-1 flex-container-column justify-content-center"
                loading={isLoadingMessages}
                error={isMessagesLoadingError || isNotFoundError}
                errorMessage={
                    isNotFoundError
                        ? trans().cabinet.chats.error.chat_not_found
                        : undefined
                }
                onReload={isNotFoundError ? undefined : loadChatMessages}
                render={() => (
                    <CardBody className="p-0 flex-1 flex-container-column overflow-hidden">
                        <FadeSwitch transitionKey={messages.length > 0}>
                            {messages.length > 0 ? (
                                <ChatMessagesList
                                    miniChat={miniChat}
                                    messages={messages}
                                    isAllMessagesLoaded={isAllMessagesLoaded}
                                    isLoadingMoreMessages={isLoadingMoreMessages}
                                    loadMoreMessages={() => loadChatMessages(true)}
                                    onScroll={readNotViewedMessages}
                                    Prepend={chat?.ad && chat?.executor && (
                                        <ChatDetailsExecutorForAd
                                            executor={chat.executor}
                                        />
                                    )}
                                />
                            ) : (
                                <div className="p-4 flex-1 text-center">
                                    {trans().cabinet.chats.no_messages}
                                </div>
                            )}
                        </FadeSwitch>
                    </CardBody>
                )}
            />
            <Collapse
                show={chat !== null}
                showImmediately
            >
                {chat && (
                    <ChatDetailsCardFooter
                        chat={chat}
                        disabled={isProcessing}
                        onNewMessageAdded={onNewMessageAdded}
                        readNotViewedMessages={readNotViewedMessages}
                        canSendImages={canSendImages}
                    />
                )}
            </Collapse>
            <ChatDetailsWebsocketHandlers
                miniChat={miniChat}
                chat={chat}
                disabled={isLoadingMessages}
                setMessages={setMessages}
                setIsReadingMessagesAllowed={setIsReadingMessagesAllowed}
            />
        </Card>
    )
}
